import React from "react";
import useTheme from "../../lib/useTheme";
import styled from "styled-components";
import { mergeStrings } from "../../lib/helpers";
import useScreenDimensions from "../../lib/hooks/useScreenDimensions";

type SliderSize = "small" | "medium";

export interface DGA_SliderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "style"> {
  label?: React.ReactNode;
  helperText?: React.ReactNode;
  size?: SliderSize;
  percentage: [number, number];
  onChnage?: Function;
}

const RangeSlider: React.FC<DGA_SliderProps> = ({
  label = "",
  helperText = "",
  size = "medium",
  percentage = [0, 0],
  onChnage,
  ...props
}) => {
  const theme = useTheme();
  const screenDimensions = useScreenDimensions();

  const sliderBaseRef = React.useRef<HTMLDivElement>(null);
  const sliderIndicatorRef = React.useRef<HTMLDivElement>(null);
  const sliderIndicatorRef2 = React.useRef<HTMLDivElement>(null);
  const [isMoving1, setIsMoving1] = React.useState(false);
  const [isMoving2, setIsMoving2] = React.useState(false);
  const [currentX1, setCurrentX1] = React.useState(0);
  const [currentX2, setCurrentX2] = React.useState(0);
  const [canMoveFromTo, setCanMoveFromTo] = React.useState([0, 0]);

  const indicatorWidth = size === "medium" ? 16 : 12;

  React.useEffect(() => {
    const percentage0 = ((currentX1 - canMoveFromTo[0]) * 100) / (canMoveFromTo[1] - canMoveFromTo[0]);
    const percentageResult = [Math.round(percentage0), percentage[1]];

    onChnage?.(percentageResult);
  }, [currentX1, canMoveFromTo]);

  React.useEffect(() => {
    const percentage1 = ((currentX2 - canMoveFromTo[0]) * 100) / (canMoveFromTo[1] - canMoveFromTo[0]);
    const percentageResult = [percentage[0], Math.round(percentage1)];

    onChnage?.(percentageResult);
  }, [currentX2, canMoveFromTo]);

  // Set restrictions
  React.useEffect(() => {
    if (sliderBaseRef.current && sliderIndicatorRef.current && sliderIndicatorRef2.current) {
      const sliderRect = sliderBaseRef.current.getBoundingClientRect();
      const indicatorRect = sliderIndicatorRef.current.getBoundingClientRect();
      const indicatorRect2 = sliderIndicatorRef2.current.getBoundingClientRect();

      setCanMoveFromTo([sliderRect.left, sliderRect.right]);
      setCurrentX1(indicatorRect.x + indicatorWidth / 2);
      setCurrentX2(indicatorRect2.x + indicatorWidth / 2);
    }
  }, [sliderBaseRef, sliderIndicatorRef, sliderIndicatorRef2, screenDimensions]);

  // Track indicator start movements
  React.useEffect(() => {
    if (isMoving1) {
      const mouseMoveHandler = (e: MouseEvent) => {
        const indicatorRect2 = sliderIndicatorRef2.current?.getBoundingClientRect()!;
        const newClientX = e.clientX;

        if (newClientX > canMoveFromTo[0] && newClientX < indicatorRect2.left + indicatorWidth / 2) {
          setCurrentX1(newClientX);
        }

        if (newClientX <= canMoveFromTo[0]) {
          setCurrentX1(canMoveFromTo[0]);
        }
        if (newClientX >= indicatorRect2.left + indicatorWidth / 2) {
          setCurrentX1(indicatorRect2.left + indicatorWidth / 2);
        }
      };

      const mouseUpHandler = () => {
        document.getElementsByTagName("body")[0].classList.remove("dgauiDragging");
        setIsMoving1(false);
      };

      window.addEventListener("mousemove", mouseMoveHandler);
      window.addEventListener("mouseup", mouseUpHandler);

      return () => {
        window.removeEventListener("mousemove", mouseMoveHandler);
        window.removeEventListener("mouseup", mouseUpHandler);
      };
    }
  }, [isMoving1, screenDimensions]);

  // Track indicator end movements
  React.useEffect(() => {
    if (isMoving2) {
      const mouseMoveHandler = (e: MouseEvent) => {
        const indicatorRect = sliderIndicatorRef.current?.getBoundingClientRect()!;
        const newClientX = e.clientX;

        if (newClientX > indicatorRect.left + indicatorWidth / 2 && newClientX < canMoveFromTo[1]) {
          setCurrentX2(newClientX);
        }
        if (newClientX <= indicatorRect.left + indicatorWidth / 2) {
          setCurrentX2(indicatorRect.left + indicatorWidth / 2);
        }
        if (newClientX >= canMoveFromTo[1]) {
          setCurrentX2(canMoveFromTo[1]);
        }
      };

      const mouseUpHandler = () => {
        document.getElementsByTagName("body")[0].classList.remove("dgauiDragging");
        setIsMoving2(false);
      };

      window.addEventListener("mousemove", mouseMoveHandler);
      window.addEventListener("mouseup", mouseUpHandler);

      return () => {
        window.removeEventListener("mousemove", mouseMoveHandler);
        window.removeEventListener("mouseup", mouseUpHandler);
      };
    }
  }, [isMoving2, screenDimensions]);

  return (
    <StyledComponent
      $size={size}
      $theme={theme}
      $percentage={percentage}
      $indicatorWidth={indicatorWidth}
      className={mergeStrings("dgaui dgaui_rangeSlider", props.className)}
      {...props}
    >
      {label && <label>{label}</label>}
      <div className="dgaui_rangeSliderContainer">
        <div className="dgaui_percentageText">{percentage[0]}</div>
        <div className="sliderBaseContent">
          <div className="dgaui sliderBase" ref={sliderBaseRef}>
            <div
              ref={sliderIndicatorRef}
              className="dgaui sliderIndicator"
              onMouseDown={() => {
                document.getElementsByTagName("body")[0].classList.add("dgauiDragging");
                setIsMoving1(true);
              }}
            />
            <div
              ref={sliderIndicatorRef2}
              className="dgaui sliderIndicator end"
              onMouseDown={() => {
                document.getElementsByTagName("body")[0].classList.add("dgauiDragging");
                setIsMoving2(true);
              }}
            />
          </div>
        </div>
        <div className="dgaui_percentageText">{percentage[1]}</div>
      </div>
      {helperText && <div className="helperText">{helperText}</div>}
    </StyledComponent>
  );
};

const StyledComponent = styled.div<{
  $theme: Theme;
  $size: SliderSize;
  $percentage: [number, number];
  $indicatorWidth: number;
}>`
  direction: ${(p) => p.$theme.direction};
  width: 100%;

  .dgaui_rangeSliderContainer {
    direction: ltr;
    display: flex;
    align-items: center;
  }

  .sliderBaseContent {
    flex: 1;
    display: flex;
    align-items: center;
  }

  .dgaui_percentageText {
    min-width: 40px;
    text-align: center;
  }

  .sliderBase {
    flex: 1;
    margin: 8px 0;
    height: ${(p) => (p.$size === "medium" ? 8 : 4)}px;
    background-color: ${(p) => p.$theme.palette.neutral[100]};
    border-radius: 4px;
    position: relative;
    margin-inline-end: 12px;
    margin-inline-start: 12px;

    &::before {
      position: absolute;
      display: block;
      content: "";
      top: 0;
      z-index: 1;
      border-radius: 4px;
      height: ${(p) => (p.$size === "medium" ? 8 : 4)}px;
      background-color: ${(p) => p.$theme.palette.primary[600]};
      width: ${(p) => p.$percentage[1] - +p.$percentage[0]}%;
      left: ${(p) => p.$percentage[0]}%;
    }

    .sliderIndicator {
      -webkit-user-select: none; /* Safari */
      -ms-user-select: none; /* IE 10+ */
      user-select: none; /* Standard syntax */

      width: ${(p) => (p.$size === "medium" ? 16 : 12)}px;
      height: ${(p) => (p.$size === "medium" ? 16 : 12)}px;
      border-radius: 100%;
      background-color: ${(p) => p.$theme.palette.primary[600]};
      position: absolute;
      cursor: pointer;
      z-index: ${(p) => (p.$percentage[0] === 100 ? 3 : 1)};
      top: calc(50% - ${(p) => p.$indicatorWidth / 2}px);
      left: calc(${(p) => p.$percentage[0]}% - ${(p) => p.$indicatorWidth / 2}px);

      &:active {
        cursor: grabbing;
      }

      &.end {
        left: calc(${(p) => p.$percentage[1]}% - ${(p) => p.$indicatorWidth / 2}px);
        z-index: 2;
      }
    }
  }
`;

export default RangeSlider;
